import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import { MaskedLine } from "../components/site/Reveal";
import { usePageMeta, API, PHONE, PHONE_TEL, EMAIL } from "../lib/seo";

const services = ["Drug Testing", "Alcohol Testing", "Fingerprinting", "Compliance Consulting", "Multiple Services"];

const initial = { full_name: "", company_name: "", email: "", phone: "", service_needed: "", message: "" };

export default function Contact() {
  usePageMeta(
    "Contact | VeraScreen Solutions — DC & Maryland Screening Services",
    "Contact VeraScreen Solutions for drug testing, alcohol screening, fingerprinting, and compliance consulting in Washington DC and Maryland. We respond within one business day."
  );

  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_needed) return toast.error("Please select a service.");
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We'll respond within one business day.");
      setForm(initial);
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="relative overflow-hidden bg-[#0E7B6B] py-24 md:py-28">
        <div className="grain-overlay" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.1}>Let's Talk</MaskedLine>
          </h1>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 lg:grid-cols-5 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }} className="lg:col-span-2">
            <div className="space-y-8">
              <div className="flex gap-4">
                <Phone size={22} className="mt-0.5 shrink-0 text-[#0E7B6B]" strokeWidth={1.5} />
                <div>
                  <p className="font-display font-bold text-[#1A3A6B]">Phone</p>
                  <a href={PHONE_TEL} data-testid="contact-phone" className="mt-1 block text-sm text-[#0E7B6B] hover:underline">{PHONE}</a>
                </div>
              </div>
              <div className="flex gap-4">
                <Mail size={22} className="mt-0.5 shrink-0 text-[#0E7B6B]" strokeWidth={1.5} />
                <div>
                  <p className="font-display font-bold text-[#1A3A6B]">Email</p>
                  <a href={`mailto:${EMAIL}`} data-testid="contact-email" className="mt-1 block text-sm text-[#0E7B6B] hover:underline">
                    {EMAIL}
                  </a>
                </div>
              </div>
              <div className="flex gap-4">
                <MapPin size={22} className="mt-0.5 shrink-0 text-[#0E7B6B]" strokeWidth={1.5} />
                <div>
                  <p className="font-display font-bold text-[#1A3A6B]">Service Area</p>
                  <p className="mt-1 text-sm text-[#333333]/80">Washington DC | Maryland | Northern Virginia</p>
                </div>
              </div>
              <div className="flex gap-4">
                <Clock size={22} className="mt-0.5 shrink-0 text-[#0E7B6B]" strokeWidth={1.5} />
                <div>
                  <p className="font-display font-bold text-[#1A3A6B]">Hours</p>
                  <p className="mt-1 text-sm text-[#333333]/80">Monday through Friday, 8:00 AM to 5:00 PM</p>
                  <p className="mt-2 flex items-start gap-1.5 text-sm font-semibold text-[#B8962E]">
                    <AlertCircle size={15} className="mt-0.5 shrink-0" />
                    After-hours available for post-accident testing — call directly
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25, ease: [0.22, 1, 0.36, 1] }} className="lg:col-span-3">
            <form onSubmit={submit} data-testid="contact-form" className="border border-gray-200 bg-[#F5F5F5] p-8 md:p-10">
              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <label className="vs-label">Full Name *</label>
                  <input required data-testid="contact-input-name" className="vs-input" value={form.full_name} onChange={set("full_name")} placeholder="Jane Smith" />
                </div>
                <div>
                  <label className="vs-label">Company Name *</label>
                  <input required data-testid="contact-input-company" className="vs-input" value={form.company_name} onChange={set("company_name")} placeholder="Acme Logistics" />
                </div>
                <div>
                  <label className="vs-label">Email Address *</label>
                  <input required type="email" data-testid="contact-input-email" className="vs-input" value={form.email} onChange={set("email")} placeholder="jane@company.com" />
                </div>
                <div>
                  <label className="vs-label">Phone Number *</label>
                  <input required type="tel" data-testid="contact-input-phone" className="vs-input" value={form.phone} onChange={set("phone")} placeholder="(202) 555-0100" />
                </div>
                <div className="md:col-span-2">
                  <label className="vs-label">Service Needed *</label>
                  <select required data-testid="contact-select-service" className="vs-input" value={form.service_needed} onChange={set("service_needed")}>
                    <option value="">Select a service…</option>
                    {services.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="vs-label">Message</label>
                  <textarea rows={5} data-testid="contact-input-message" className="vs-input resize-none" value={form.message} onChange={set("message")} placeholder="Tell us about your screening needs…" />
                </div>
              </div>
              <button type="submit" disabled={sending} data-testid="contact-submit-btn" className="btn-gold mt-8 w-full disabled:opacity-60 md:w-auto">
                {sending ? "Sending…" : "Send Message"}
              </button>
              <p className="mt-5 text-xs leading-relaxed text-gray-500">
                We respond to all inquiries within one business day. For post-accident or urgent
                testing needs, please call us directly.
              </p>
            </form>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
